import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TrendingUp, Users2, Layers, CalendarCheck, Percent, Timer, GraduationCap, X } from "lucide-react";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";

const PRESETS = [
  { v: "week", l: "Settimana" },
  { v: "4w", l: "4 settimane" },
  { v: "month", l: "Mese" },
  { v: "season", l: "Stagione" },
];

function toISO(d) { return d.toISOString().slice(0, 10); }
function computePeriod(preset) {
  const end = new Date();
  const start = new Date();
  if (preset === "week") start.setDate(end.getDate() - 7);
  else if (preset === "4w") start.setDate(end.getDate() - 28);
  else if (preset === "season") { start.setMonth(7); start.setDate(1); if (start > end) start.setFullYear(start.getFullYear() - 1); }
  else { start.setDate(1); }
  return { from: toISO(start), to: toISO(end) };
}

export default function TrainingStats() {
  const [preset, setPreset] = useState("season");
  const [weapon, setWeapon] = useState("all");
  const [groupId, setGroupId] = useState("all");
  const [staffId, setStaffId] = useState("all");
  const [athleteId, setAthleteId] = useState("all");
  const [groups, setGroups] = useState([]);
  const [staff, setStaff] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // athlete_id for drill-down

  useEffect(() => {
    api.get("/groups").then((r) => setGroups(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setStaff((r.data || []).filter((u) => ["coach", "admin", "trainer"].includes(u.role)))).catch(() => {});
    api.get("/athletes?include_archived=1").then((r) => setAthletes((r.data || []).filter((a) => !a.archived))).catch(() => {});
  }, []);

  const period = useMemo(() => computePeriod(preset), [preset]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { ...period };
    if (weapon !== "all") params.weapon = weapon;
    if (groupId !== "all") params.group_id = groupId;
    if (staffId !== "all") params.staff_id = staffId;
    if (athleteId !== "all") params.athlete_id = athleteId;
    try {
      const { data } = await api.get("/training/analytics", { params });
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [period, weapon, groupId, staffId, athleteId]);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const maxDistro = useMemo(() => Math.max(1, ...(data?.activity_distribution || []).map((d) => d.minutes)), [data]);
  const maxTrendP = useMemo(() => Math.max(1, ...(data?.trend || []).map((b) => b.present)), [data]);
  const maxTrendM = useMemo(() => Math.max(1, ...(data?.trend || []).map((b) => b.minutes)), [data]);

  return (
    <div className="space-y-6" data-testid="training-stats">
      {/* Filtri */}
      <div className="flex items-end gap-3 flex-wrap">
        <Field label="Periodo"><Sel v={preset} on={setPreset} testid="stats-period" w="w-36" opts={PRESETS.map((p) => [p.v, p.l])} /></Field>
        <Field label="Arma"><Sel v={weapon} on={setWeapon} testid="stats-weapon" w="w-32" opts={[["all", "Tutte"], ["spada", "Spada"], ["sciabola", "Sciabola"], ["fioretto", "Fioretto"]]} /></Field>
        <Field label="Gruppo"><Sel v={groupId} on={setGroupId} testid="stats-group" w="w-48" opts={[["all", "Tutti"], ...groups.map((g) => [g.id, g.name])]} /></Field>
        <Field label="Tecnico"><Sel v={staffId} on={setStaffId} testid="stats-staff" w="w-44" opts={[["all", "Tutti"], ...staff.map((u) => [u.id, u.name])]} /></Field>
        <Field label="Atleta"><Sel v={athleteId} on={setAthleteId} testid="stats-athlete" w="w-48" opts={[["all", "Tutti"], ...sortAthletes(athletes).map((a) => [a.id, displayName(a)])]} /></Field>
        {data?.period && <div className="text-xs text-zinc-500 pb-2 font-mono">{data.period.from} → {data.period.to}</div>}
      </div>

      {loading && <div className="text-zinc-500 py-12 text-center">Caricamento…</div>}
      {!loading && data && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            <Kpi label="Sessioni svolte" value={s.sessions_done || 0} icon={CalendarCheck} testid="kpi-sessions" />
            <Kpi label="Atleti presenti" value={s.athletes_present || 0} icon={Users2} testid="kpi-athletes" />
            <Kpi label="Tasso presenza" value={`${s.attendance_rate || 0}%`} icon={Percent} testid="kpi-rate" />
            <Kpi label="Tecnici attivi" value={s.technicians_active || 0} icon={GraduationCap} testid="kpi-techs" />
            <Kpi label="Minuti attività" value={s.activity_minutes || 0} icon={Timer} testid="kpi-minutes" />
            <Kpi label="Attività gruppo" value={s.group_activities || 0} icon={Layers} testid="kpi-group-act" />
            <Kpi label="Lezioni individuali" value={s.individual_lessons || 0} icon={GraduationCap} testid="kpi-lessons" />
          </div>

          {/* Statistiche di gruppo */}
          <Section title="Statistiche di gruppo" icon={Users2} testid="stats-groups">
            {(data.groups || []).length === 0 ? <Empty label="Nessuna sessione svolta nel periodo" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="py-2 pr-3">Gruppo</th><th className="py-2 pr-3">Sessioni</th>
                      <th className="py-2 pr-3">Media presenti</th><th className="py-2 pr-3">% presenza</th>
                      <th className="py-2 pr-3">Minuti</th><th className="py-2 pr-3">Lezioni</th>
                      <th className="py-2 pr-3">Atleti con lezione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.groups.map((g) => (
                      <tr key={g.group_id} data-testid={`group-stat-${g.group_id}`} className="border-t border-zinc-100">
                        <td className="py-2 pr-3 font-medium">{g.name}</td>
                        <td className="py-2 pr-3 font-mono">{g.sessions}</td>
                        <td className="py-2 pr-3 font-mono">{g.avg_present}</td>
                        <td className="py-2 pr-3"><span className={`font-mono ${g.attendance_pct >= 75 ? "text-emerald-700" : g.attendance_pct >= 50 ? "text-amber-700" : "text-red-700"}`}>{g.attendance_pct}%</span></td>
                        <td className="py-2 pr-3 font-mono">{g.minutes}′</td>
                        <td className="py-2 pr-3 font-mono">{g.lessons}</td>
                        <td className="py-2 pr-3 font-mono">{g.athletes_with_lesson}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Distribuzione */}
            <Section title="Distribuzione del lavoro per categoria" icon={Layers} testid="stats-distribution">
              {(data.activity_distribution || []).length === 0 ? <Empty label="Nessuna attività categorizzata nel periodo" /> : (
                <div className="space-y-2">
                  {data.activity_distribution.map((d) => (
                    <div key={d.category}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium">{d.category}</span>
                        <span className="text-zinc-500 font-mono">{d.minutes}′ · {d.percentage}%</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-black" style={{ width: `${Math.min(100, 100 * d.minutes / maxDistro)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Trend */}
            <Section title="Andamento settimanale" icon={TrendingUp} testid="stats-trend">
              {(data.trend || []).length === 0 ? <Empty label="Nessun dato nel periodo" /> : (
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                    <tr><th className="py-2 pr-3">Settimana</th><th className="py-2 pr-3">Presenze</th><th className="py-2 pr-3">Minuti</th><th className="py-2 pr-3">Lezioni</th></tr>
                  </thead>
                  <tbody>
                    {data.trend.map((b) => (
                      <tr key={b.bucket} className="border-t border-zinc-100">
                        <td className="py-2 pr-3 font-mono">{b.bucket}</td>
                        <td className="py-2 pr-3"><Bar value={b.present} max={maxTrendP} suffix="" /></td>
                        <td className="py-2 pr-3"><Bar value={b.minutes} max={maxTrendM} suffix="′" tone="zinc" /></td>
                        <td className="py-2 pr-3 font-mono text-zinc-600">{b.lessons}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
          </div>

          {/* Drill-down atleta */}
          <Section title="Drill-down atleta" icon={Users2} testid="stats-drilldown">
            <p className="text-xs text-zinc-500 mb-2">Seleziona un atleta per vedere presenze, % frequenza, lezioni ricevute e andamento.</p>
            <Sel v={athleteId === "all" ? "" : athleteId} on={(v) => setDrill(v)} testid="drill-athlete" w="w-64"
              placeholder="Seleziona atleta…" opts={athletes.map((a) => [a.id, `${a.first_name} ${a.last_name}`])} />
          </Section>
        </>
      )}

      {drill && <AthleteDrill athleteId={drill} period={period} onClose={() => setDrill(null)} />}
    </div>
  );
}

function AthleteDrill({ athleteId, period, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/training/athlete/${athleteId}/attendance`, { params: period })
      .then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [athleteId, period]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="athlete-drill">
        <DialogHeader><DialogTitle>{d?.athlete ? `${d.athlete.first_name} ${d.athlete.last_name}` : "Atleta"}</DialogTitle></DialogHeader>
        {!d ? <div className="py-8 text-center text-zinc-500">Caricamento…</div> :
          d.error || !d.athlete ? <div className="py-8 text-center text-zinc-500">Dati non disponibili.</div> : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <Mini label="Presenze" v={d.totals.present} testid="drill-present" />
                <Mini label="Assenze" v={d.totals.absent} testid="drill-absent" />
                <Mini label="% Frequenza" v={d.totals.attendance_pct != null ? `${d.totals.attendance_pct}%` : "—"} testid="drill-pct" />
                <Mini label="Lezioni ricevute" v={d.totals.lessons_received} testid="drill-lessons" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Sessioni frequentate ({d.totals.sessions})</div>
                {d.events.length === 0 ? <Empty label="Nessuna presenza registrata nel periodo" /> : (
                  <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 max-h-48 overflow-y-auto">
                    {d.events.map((e, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm">
                        <span className="font-mono text-xs">{e.date}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${["presente", "ritardo", "parziale"].includes(e.status) ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{e.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {d.lessons.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Lezioni individuali</div>
                  <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 max-h-40 overflow-y-auto">
                    {d.lessons.map((l) => (
                      <div key={l.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                        <span className="font-mono text-xs">{(l.date || "").slice(0, 10)}</span>
                        <span className="text-zinc-600 truncate max-w-xs">{l.objective || "—"}</span>
                        <span className="font-mono text-xs">{l.duration || 0}′</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div className="flex flex-col"><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>{children}</div>;
}
function Sel({ v, on, testid, w, opts, placeholder }) {
  return (
    <Select value={v} onValueChange={on}>
      <SelectTrigger data-testid={testid} className={`h-10 ${w}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">{opts.map(([val, lab]) => <SelectItem key={val} value={val}>{lab}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</div>
      <div className="font-heading text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
function Mini({ label, v, testid }) {
  return <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid={testid}><div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div><div className="font-heading text-2xl font-bold mt-1">{v}</div></div>;
}
function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">{Icon && <Icon className="w-4 h-4 text-zinc-400" />}<h3 className="font-heading text-lg font-semibold">{title}</h3></div>
      {children}
    </div>
  );
}
function Bar({ value, max, suffix, tone }) {
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden"><div className={`h-full ${tone === "zinc" ? "bg-zinc-400" : "bg-black"}`} style={{ width: `${100 * value / max}%` }} /></div>
      <span className="font-mono text-xs w-12 text-right">{value}{suffix}</span>
    </div>
  );
}
function Empty({ label }) { return <div className="text-sm text-zinc-500 py-6 text-center">{label}</div>; }
